import { useState } from 'react';
import { Link2, Unlink } from 'lucide-react';
import { TEXT_PATH_PRESETS } from '../../data/textPathPresets';
import type { TextElement, TextPathPreset, CanvasElement, PathElement, TextPathConfig } from '../../types';

interface TextPathControlsProps {
  element: TextElement;
  elements: CanvasElement[];
  onUpdate: (updates: Partial<TextElement>) => void;
}

export default function TextPathControls({ element, elements, onUpdate }: TextPathControlsProps) {
  const [activeCategory, setActiveCategory] = useState<'arc' | 'wave' | 'circle'>('arc');
  const [mode, setMode] = useState<'preset' | 'element'>(element.textPath ? 'element' : 'preset');

  const hasPresetPath = !!element.pathData;
  const hasElementPath = !!element.textPath;
  const hasAnyPath = hasPresetPath || hasElementPath;

  const pathElements = elements.filter((e): e is PathElement => e.type === 'path');

  const categories: { key: typeof activeCategory; label: string }[] = [
    { key: 'arc', label: 'Arcs' },
    { key: 'wave', label: 'Waves' },
    { key: 'circle', label: 'Circles' },
  ];

  const filteredPresets = TEXT_PATH_PRESETS.filter((p) => p.category === activeCategory);

  const handlePresetClick = (preset: TextPathPreset) => {
    onUpdate({
      pathData: preset.pathData,
      pathStartOffset: element.pathStartOffset ?? 0,
      pathSide: element.pathSide ?? 'left',
      pathAlign: element.pathAlign ?? 'baseline',
      textPath: undefined,
    });
  };

  const handleAttachToPath = (pathId: string) => {
    const config: TextPathConfig = {
      pathElementId: pathId,
      startOffset: element.textPath?.startOffset ?? 0,
      side: element.textPath?.side ?? 'left',
      align: element.textPath?.align ?? 'baseline',
    };
    onUpdate({
      textPath: config,
      pathData: undefined,
      pathStartOffset: undefined,
      pathSide: undefined,
      pathAlign: undefined,
    });
  };

  const handleDetachPath = () => {
    onUpdate({
      textPath: undefined,
      pathData: undefined,
      pathStartOffset: undefined,
      pathSide: undefined,
      pathAlign: undefined,
    });
  };

  const handleElementPathUpdate = (updates: Partial<TextPathConfig>) => {
    if (!element.textPath) return;
    onUpdate({ textPath: { ...element.textPath, ...updates } });
  };

  const renderPathPreview = (pathData: string, isActive: boolean) => (
    <svg
      viewBox="0 0 300 150"
      className={`w-full h-full ${isActive ? 'stroke-cyan-400' : 'stroke-zinc-500'}`}
      fill="none"
      strokeWidth="2"
    >
      <path d={pathData} />
    </svg>
  );

  const renderAlignmentButtons = (
    currentAlign: 'baseline' | 'center' | 'ascender' | 'descender',
    onChange: (align: 'baseline' | 'center' | 'ascender' | 'descender') => void
  ) => (
    <div className="flex gap-1 bg-zinc-800/50 rounded-md p-0.5">
      {(['baseline', 'center', 'ascender', 'descender'] as const).map((align) => (
        <button
          key={align}
          onClick={() => onChange(align)}
          className={`text-xs px-1.5 py-1 rounded capitalize transition-colors ${
            currentAlign === align
              ? 'bg-cyan-500/20 text-cyan-400'
              : 'text-zinc-400 hover:text-zinc-300'
          }`}
        >
          {align === 'baseline' ? 'Base' : align === 'ascender' ? 'Asc' : align === 'descender' ? 'Desc' : 'Ctr'}
        </button>
      ))}
    </div>
  );

  const renderSideButtons = (
    currentSide: 'left' | 'right',
    onChange: (side: 'left' | 'right') => void
  ) => (
    <div className="flex gap-1 bg-zinc-800/50 rounded-md p-0.5">
      <button
        onClick={() => onChange('left')}
        className={`text-xs px-2 py-1 rounded transition-colors ${
          currentSide === 'left'
            ? 'bg-cyan-500/20 text-cyan-400'
            : 'text-zinc-400 hover:text-zinc-300'
        }`}
      >
        Above
      </button>
      <button
        onClick={() => onChange('right')}
        className={`text-xs px-2 py-1 rounded transition-colors ${
          currentSide === 'right'
            ? 'bg-cyan-500/20 text-cyan-400'
            : 'text-zinc-400 hover:text-zinc-300'
        }`}
      >
        Below
      </button>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-0.5">
        <button
          onClick={() => setMode('preset')}
          className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${
            mode === 'preset'
              ? 'bg-cyan-500/20 text-cyan-400'
              : 'text-zinc-400 hover:text-zinc-300'
          }`}
        >
          Presets
        </button>
        <button
          onClick={() => setMode('element')}
          className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${
            mode === 'element'
              ? 'bg-cyan-500/20 text-cyan-400'
              : 'text-zinc-400 hover:text-zinc-300'
          }`}
        >
          Path Element
        </button>
      </div>

      {mode === 'preset' && (
        <>
          <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-0.5">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${
                  activeCategory === cat.key
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-zinc-400 hover:text-zinc-300'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-1.5">
            {filteredPresets.map((preset) => {
              const isActive = element.pathData === preset.pathData;
              return (
                <button
                  key={preset.id}
                  onClick={() => handlePresetClick(preset)}
                  className={`aspect-[2/1] rounded-md border transition-all p-1 ${
                    isActive
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-500'
                  }`}
                  title={preset.name}
                >
                  {renderPathPreview(preset.pathData, isActive)}
                </button>
              );
            })}
          </div>

          {hasPresetPath && (
            <div className="space-y-3 pt-2 border-t border-zinc-700/50">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-zinc-400">Start Offset</label>
                  <span className="text-xs text-zinc-500 tabular-nums">{element.pathStartOffset ?? 0}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={500}
                  value={element.pathStartOffset ?? 0}
                  onChange={(e) => onUpdate({ pathStartOffset: Number(e.target.value) })}
                  className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-xs text-zinc-400">Text Side</label>
                {renderSideButtons(
                  element.pathSide ?? 'left',
                  (side) => onUpdate({ pathSide: side })
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="text-xs text-zinc-400">Alignment</label>
                {renderAlignmentButtons(
                  element.pathAlign ?? 'baseline',
                  (align) => onUpdate({ pathAlign: align })
                )}
              </div>

              <div className="bg-zinc-800/50 rounded-lg p-2 border border-zinc-700/50">
                <div className="text-xs text-zinc-500 mb-1">Current Path</div>
                <svg viewBox="0 0 300 150" className="w-full h-12 stroke-cyan-400" fill="none" strokeWidth="2">
                  <path d={element.pathData} />
                </svg>
              </div>
            </div>
          )}
        </>
      )}

      {mode === 'element' && (
        <div className="space-y-3">
          {pathElements.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-4">
              No path elements on canvas. Draw a path with the Pen tool first.
            </div>
          ) : (
            <div className="space-y-1">
              {pathElements.map((pathEl) => {
                const isAttached = element.textPath?.pathElementId === pathEl.id;
                return (
                  <button
                    key={pathEl.id}
                    onClick={() => handleAttachToPath(pathEl.id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md border transition-all ${
                      isAttached
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-500'
                    }`}
                  >
                    <Link2 size={12} className={isAttached ? 'text-cyan-400' : 'text-zinc-500'} />
                    <span className="text-xs text-zinc-300 truncate flex-1 text-left">
                      {pathEl.name || `Path ${pathEl.id.slice(0, 6)}`}
                    </span>
                    <svg viewBox="0 0 60 30" className={`w-8 h-4 ${isAttached ? 'stroke-cyan-400' : 'stroke-zinc-500'}`} fill="none" strokeWidth="1.5">
                      <path d={pathEl.pathData} transform={`scale(${60 / Math.max(pathEl.width, 1)} ${30 / Math.max(pathEl.height, 1)})`} />
                    </svg>
                  </button>
                );
              })}
            </div>
          )}

          {hasElementPath && (
            <div className="space-y-3 pt-2 border-t border-zinc-700/50">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-zinc-400">Start Offset</label>
                  <span className="text-xs text-zinc-500 tabular-nums">{element.textPath!.startOffset}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={500}
                  value={element.textPath!.startOffset}
                  onChange={(e) => handleElementPathUpdate({ startOffset: Number(e.target.value) })}
                  className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-xs text-zinc-400">Text Side</label>
                {renderSideButtons(
                  element.textPath!.side,
                  (side) => handleElementPathUpdate({ side })
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="text-xs text-zinc-400">Alignment</label>
                {renderAlignmentButtons(
                  element.textPath!.align,
                  (align) => handleElementPathUpdate({ align })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {hasAnyPath && (
        <button
          onClick={handleDetachPath}
          className="w-full flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <Unlink size={12} />
          Remove Path
        </button>
      )}
    </div>
  );
}
